import React, { useState, useEffect } from "react";
import { AdminLayout } from "@autoserve/woosent.woosent.admin-layout";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

import Audiences from "./steps/Audiences";
import Message from "./steps/Message";
import Review from "./steps/Review";
import SuccessBanner from "./components/SuccessBanner";
import HelperPopoverButton from "./components/HelperPopoverButton";
import IphoneMessagePreview from "./components/IphoneMessagePreview/IphoneMessagePreview";

import { sendBulkSms, sendBulkMms, uploadImageToS3 } from "./utils/api";
import { fileToDataUri } from "./utils/helpers";

const Campaigns = () => {
	const { accountSid, authToken, version, fromPhone } =
		// @ts-ignore
		window.adminUiAppContext || {};

	const [activeStep, setActiveStep] = useState(0);
	const [message, setMessage] = useState("");
	const [phones, setPhones] = useState<string[]>([]);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [sendSuccess, setSendSuccess] = useState(false);
	const [image, setImage] = useState<File | null>(null);
	const [imageUri, setImageUri] = useState("");
	const [s3Url, setS3Url] = useState("");
	const [sentFailedPhones, setSentFailedPhones] = useState<string[]>([]);

	const handleNext = (value: string | string[]) => {
		if (Boolean(value.length)) {
			setActiveStep((prevActiveStep) => prevActiveStep + 1);
		} else {
			setError(
				"Please fill out the dedicated field to proceed with sending SMS",
			);
		}
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const sendMessage = async (numbers: string[]) => {
		setLoading(true);
		console.log("Sending message:", message, "to: ", numbers);
		try {
			if (Boolean(s3Url)) {
				const res = await sendBulkMms(
					numbers,
					message,
					accountSid,
					authToken,
					fromPhone,
					s3Url,
				).then((res) => {
					setLoading(false);
					return res;
				});

				if (res.status === 200) {
					const messagesRes = res.data.messages;

					messagesRes.forEach((message: any) => {
						if (message.status === "error" && Boolean(message.error)) {
							setSentFailedPhones([
								...sentFailedPhones,
								message.error.phoneNumber,
							]);
						}
					});

					setSendSuccess(true);
					console.log("API response :>", res.data);
				}
			} else {
				const res = await sendBulkSms(
					numbers,
					message,
					accountSid,
					authToken,
					fromPhone,
				).then((res) => {
					setLoading(false);
					return res;
				});

				if (res.status === 200) {
					const messagesRes = res.data.messages;

					messagesRes.forEach((message: any) => {
						if (message.status === "error" && Boolean(message.error)) {
							setSentFailedPhones([
								...sentFailedPhones,
								message.error.phoneNumber,
							]);
						}
					});

					setSendSuccess(true);
					console.log("API response :>", res.data);
				}
			}
		} catch (e) {
			console.error(e);
			setError(
				"Error sending SMS to audiences. Please try again or contact the MessageFlow team.",
			);
			setLoading(false);
		}
	};

	const handleReset = () => {
		setPhones([]);
		setMessage("");
		setActiveStep(0);
		setSendSuccess(false);
		setS3Url("");
		setImageUri("");
		setImage(null);
	};

	useEffect(() => {
		setError("");
	}, [activeStep]);

	useEffect(() => {
		if (Boolean(image)) {
			fileToDataUri(image as File).then((dataUri) => setImageUri(dataUri));
			if (Boolean(s3Url)) {
				setS3Url("");
			}
			uploadImageToS3(image as File)?.then((value) => {
				setS3Url(value.Location);
			});
		}
	}, [image]);

	const steps: {
		label: string;
		description: string;
		content: React.ReactNode;
		helper?: React.ReactNode;
	}[] = [
		{
			label: "Audiences",
			description:
				"Add one or multiple phone number separated by a comma or a space. ",
			content: (
				<Audiences
					next={handleNext}
					phones={phones}
					setPhones={setPhones}
					error={error}
					setError={setError}
				/>
			),
			helper: (
				<Typography sx={{ p: 2 }}>
					Enter any valid phone number format, e.g: (866) 333-4444. <br /> You
					can also enter multiple phone numbers separated by a comma, e.g:
					+18663334444, (866)3335555, +1 (866) 333-2222, etc.
					<br /> Only +, (), -, and commas are accepted as special characters.
				</Typography>
			),
		},
		{
			label: "Message",
			description: "Enter the message you want to send.",
			content: (
				<Message
					next={handleNext}
					goBack={handleBack}
					message={message}
					setMessage={setMessage}
					error={error}
					setError={setError}
					image={image}
					setImage={setImage}
					setImageUri={setImageUri}
					setS3Url={setS3Url}
				/>
			),
			helper: (
				<Typography variant='subtitle2' sx={{ p: 2 }}>
					Drafting the message to send to the audience. <br />
					<br /> <b>Note:</b> The character limit for a single SMS message is
					technically 160 characters. <br /> The message that longer than 160
					characters will be delivered separately and could be combine to
					display for the audience.
				</Typography>
			),
		},
		{
			label: "Review",
			description: Boolean(phones.length)
				? `This campaign will be sent to ${phones.length} phone numbers.`
				: "Review your campaign.",
			content: (
				<Review
					phones={phones}
					message={message}
					goBack={handleBack}
					sendMessage={sendMessage}
					loading={loading}
					error={error}
					imageAttachment={s3Url}
				/>
			),
		},
	];

	return (
		<Grid
			container
			gap={3}
			marginTop={3}
			justifyContent={sendSuccess ? "center" : undefined}>
			<Grid item lg={12} sx={{ display: sendSuccess ? undefined : "none" }}>
				<SuccessBanner
					handleReset={handleReset}
					sentFailedPhones={sentFailedPhones}
				/>
			</Grid>

			<Grid item lg={6} sx={{ display: sendSuccess ? "none" : undefined }}>
				<Stepper activeStep={activeStep} orientation='vertical'>
					{steps.map((step, index) => (
						<Step key={step.label}>
							<StepLabel
								optional={
									<Grid container alignItems='center'>
										<Grid item md={12}>
											<Typography variant='subtitle2' color='#00000099'>
												{step.description}
												{Boolean(phones.length) &&
												index !== activeStep &&
												step.label === "Audiences" ? (
													<Typography
														variant='subtitle1'
														color='#000000de'
														sx={{
															display: "inline",
															fontWeight: 600,
														}}>
														{phones.length} Recipients
													</Typography>
												) : null}
											</Typography>
										</Grid>
									</Grid>
								}>
								<Box sx={{ display: "flex", alignItems: "center" }}>
									<Typography variant='body1' fontWeight={600}>
										{step.label}
									</Typography>
									{Boolean(step.helper) && index === activeStep ? (
										<HelperPopoverButton>{step.helper}</HelperPopoverButton>
									) : null}
								</Box>
							</StepLabel>
							<StepContent>{step.content}</StepContent>
						</Step>
					))}
				</Stepper>
			</Grid>

			{activeStep === 1 && <Divider orientation='vertical' flexItem />}

			<Grid item lg={5}>
				{activeStep === 1 ? (
					<IphoneMessagePreview
						messageContent={message}
						imageAttachment={imageUri}
						s3Url={s3Url}
					/>
				) : null}
			</Grid>
		</Grid>
	);
};

export default Campaigns;
